import type { GetServerSideProps } from 'next'
import type { FC, ReactNode } from 'react'
import type { ModeAvailability, PracticeMode } from '@/prakrit/components/practice/practice-types'

import Link from 'next/link'
import { RefreshCw, WifiOff } from 'lucide-react'
import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import { Bi } from '@/prakrit/components/common/bi'
import { PracticeHub } from '@/prakrit/components/practice/practice-hub'
import { modeAvailability } from '@/prakrit/lib/practice/generate'
import { practiceResources } from '@/prakrit/components/practice/practice-server'
import { parseTopics, parseVikalp } from '@/prakrit/components/practice/practice-topics'

interface PracticePageProps {
    /** Which games and levels can produce a question right now (teacher-verified forms, recordings, stories). */
    availability: Record<PracticeMode, ModeAvailability>
    /** Topics chosen in the filter bar (?topic=), kept in the URL so they survive going back. */
    topics: string[]
    /** विकल्प सहित (?vikalp=1): variant forms included; the default is standard forms only. */
    vikalp: boolean
    /** The verified content could not be read just now: say so instead of showing every game as empty. */
    unavailable: boolean
}

const PracticePage: FC<PracticePageProps> = ({ availability, topics, vikalp, unavailable }): ReactNode => (
    <Layout>
        <Seo
            title='अभ्यास और खेल'
            description='Prakrit practice games in three levels: roots, tenses, tables, participles, cases, sentences, translation, sandhi, compounds and listening, graded by rule.'
        />
        {unavailable && (
            <div role='status' className='mx-auto mt-6 flex w-full max-w-3xl items-start gap-3 rounded-2xl border border-saffron/50 bg-saffron/5 px-4 py-3 text-sm sm:px-5'>
                <WifiOff className='mt-0.5 size-5 shrink-0 text-saffron-strong' aria-hidden='true' />
                <p className='flex flex-1 flex-col'>
                    <Bi
                        hi='अभ्यास अभी उपलब्ध नहीं है; आपकी प्रगति सुरक्षित है। थोड़ी देर में फिर कोशिश करें।'
                        en='Practice is not available right now. Your progress is safe; please try again in a moment.'
                    />
                </p>
                <Link href='/practice' className='inline-flex min-h-tap shrink-0 items-center gap-1.5 rounded-full border border-border bg-card px-3 font-semibold hover:border-saffron/60'>
                    <RefreshCw className='size-4' aria-hidden='true' />
                    <span lang='en'>Try again</span>
                </Link>
            </div>
        )}
        <PracticeHub availability={availability} initialTopics={topics} initialVikalp={vikalp} />
    </Layout>
)

export const getServerSideProps: GetServerSideProps<PracticePageProps> = async ({ query }) => {
    const resources = await practiceResources()
    const availability = modeAvailability(resources)

    // Props must be plain JSON (no undefined values).
    return {
        props: {
            availability: JSON.parse(JSON.stringify(availability)) as Record<PracticeMode, ModeAvailability>,
            topics: parseTopics(query.topic),
            vikalp: parseVikalp(query.vikalp),
            unavailable: Boolean(resources.unavailable)
        }
    }
}

export default PracticePage
